import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDateLocale } from "@/hooks/useDateLocale";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import {
  Bell,
  BellOff,
  MapPin,
  Gauge,
  Power,
  PowerOff,
  AlertTriangle,
  Fuel,
  Wrench,
  Calendar as CalendarIcon,
  Search,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Car,
  Settings2,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchEvents,
  fetchDevices,
  fetchNotifications,
  deleteNotification,
  type Event,
  type Device,
  type NotificationRule,
} from "@/lib/traccar-api";
import { NotificationRuleDialog } from "@/components/notifications/NotificationRuleDialog";
import { cn } from "@/lib/utils";

// Event type configuration
const EVENT_ICONS: Record<string, { icon: React.ElementType; color: string; severity: "info" | "warning" | "error" }> = {
  deviceOnline: { icon: Power, color: "text-green-500", severity: "info" },
  deviceOffline: { icon: PowerOff, color: "text-red-500", severity: "error" },
  deviceUnknown: { icon: AlertTriangle, color: "text-yellow-500", severity: "warning" },
  deviceMoving: { icon: Car, color: "text-blue-500", severity: "info" },
  deviceStopped: { icon: Car, color: "text-muted-foreground", severity: "info" },
  deviceIdle: { icon: Car, color: "text-yellow-500", severity: "warning" },
  deviceOverspeed: { icon: Gauge, color: "text-orange-500", severity: "warning" },
  geofenceEnter: { icon: MapPin, color: "text-green-500", severity: "info" },
  geofenceExit: { icon: MapPin, color: "text-red-500", severity: "warning" },
  ignitionOn: { icon: Power, color: "text-green-500", severity: "info" },
  ignitionOff: { icon: PowerOff, color: "text-muted-foreground", severity: "info" },
  fuelDrop: { icon: Fuel, color: "text-red-500", severity: "error" },
  fuelIncrease: { icon: Fuel, color: "text-green-500", severity: "info" },
  deviceFuelDrop: { icon: Fuel, color: "text-red-500", severity: "error" },
  deviceFuelIncrease: { icon: Fuel, color: "text-green-500", severity: "info" },
  alarm: { icon: AlertTriangle, color: "text-red-500", severity: "error" },
  maintenance: { icon: Wrench, color: "text-yellow-500", severity: "warning" },
  textMessage: { icon: Bell, color: "text-blue-500", severity: "info" },
  driverChanged: { icon: Car, color: "text-blue-500", severity: "info" },
  deviceInactive: { icon: PowerOff, color: "text-red-500", severity: "error" },
  commandResult: { icon: Settings2, color: "text-blue-500", severity: "info" },
  queuedCommandSent: { icon: Settings2, color: "text-blue-500", severity: "info" },
  media: { icon: Bell, color: "text-blue-500", severity: "info" },
  test: { icon: Bell, color: "text-muted-foreground", severity: "info" },
};

const getEventConfig = (type: string, t: (key: string) => string) => {
  const config = EVENT_ICONS[type] || { icon: Bell, color: "text-muted-foreground", severity: "info" as const };
  const label = t(`notifications.events.${type}`) || type;
  return { ...config, label };
};

const Notifications = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const queryClient = useQueryClient();

  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), 1),
    to: new Date(),
  });
  const [selectedDevice, setSelectedDevice] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // Configure tab state
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | undefined>(undefined);
  const [deletingRuleId, setDeletingRuleId] = useState<number | null>(null);

  const { data: devices = [] } = useQuery({
    queryKey: ["devices"],
    queryFn: fetchDevices,
  });

  const { data: notificationRules = [], isLoading: rulesLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: fetchNotifications,
  });


  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast.success(t("notifications.config.deleted"));
      setDeletingRuleId(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setDeletingRuleId(null);
    },
  });

  const getTypeLabelForRule = (type: string) => {
    const key = `notifications.events.${type}`;
    const translated = t(key);
    return translated !== key ? translated : type;
  };

  const deviceMap = useMemo(() => {
    return devices.reduce((acc, d) => {
      acc[d.id] = d;
      return acc;
    }, {} as Record<number, Device>);
  }, [devices]);

  const { data: events = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["events", dateRange.from.toISOString(), dateRange.to.toISOString()],
    queryFn: () =>
      fetchEvents(
        undefined,
        startOfDay(dateRange.from).toISOString(),
        endOfDay(dateRange.to).toISOString()
      ),
  });

  const filteredEvents = useMemo(() => {
    return events
      .filter((event) => {
        if (selectedDevice !== "all" && event.deviceId.toString() !== selectedDevice) {
          return false;
        }
        if (selectedType !== "all" && event.type !== selectedType) {
          return false;
        }
        if (activeTab !== "all") {
          const config = getEventConfig(event.type, t);
          if (config.severity !== activeTab) return false;
        }
        if (searchQuery) {
          const device = deviceMap[event.deviceId];
          const deviceName = device?.name?.toLowerCase() || "";
          const eventLabel = getEventConfig(event.type, t).label.toLowerCase();
          const query = searchQuery.toLowerCase();
          if (!deviceName.includes(query) && !eventLabel.includes(query)) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => new Date(b.eventTime).getTime() - new Date(a.eventTime).getTime());
  }, [events, selectedDevice, selectedType, activeTab, searchQuery, deviceMap, t]);

  const eventTypes = useMemo(() => {
    const types = new Set(events.map((e) => e.type));
    return Array.from(types).sort();
  }, [events]);

  const stats = useMemo(() => {
    const all = events.length;
    const errors = events.filter((e) => (EVENT_ICONS[e.type]?.severity || "info") === "error").length;
    const warnings = events.filter((e) => (EVENT_ICONS[e.type]?.severity || "info") === "warning").length;
    const info = events.filter((e) => (EVENT_ICONS[e.type]?.severity || "info") === "info").length;
    return { all, errors, warnings, info };
  }, [events]);

  const renderEvent = (event: Event) => {
    const config = getEventConfig(event.type, t);
    const device = deviceMap[event.deviceId];
    const Icon = config.icon;

    return (
      <Card key={event.id} className="hover:bg-accent/50 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className={cn("p-2 rounded-full bg-muted", config.color)}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{config.label}</span>
                <Badge
                  variant={
                    config.severity === "error"
                      ? "destructive"
                      : config.severity === "warning"
                      ? "secondary"
                      : "outline"
                  }
                  className="text-xs"
                >
                  {t(`notifications.severity.${config.severity}`)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {device?.name || `Device #${event.deviceId}`}
              </p>
              {event.attributes && Object.keys(event.attributes).length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground">
                  {event.attributes.speed !== undefined && (
                    <span className="mr-3">{t("notifications.attributes.speed")}: {((event.attributes.speed as number) * 1.852).toFixed(0)} km/h</span>
                  )}
                  {event.attributes.geofenceId !== undefined && (
                    <span className="mr-3">{t("notifications.attributes.geofence")}: #{String(event.attributes.geofenceId)}</span>
                  )}
                  {event.attributes.alarm && (
                    <span className="text-red-500">{t("notifications.attributes.alarm")}: {String(event.attributes.alarm)}</span>
                  )}
                </div>
              )}
            </div>
            <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
              <p>{format(new Date(event.eventTime), "MMM d", { locale: dateFnsLocale })}</p>
              <p>{format(new Date(event.eventTime), "HH:mm:ss")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border-b">
          <div>
            <h1 className="text-2xl font-bold">{t("notifications.title")}</h1>
            <p className="text-muted-foreground">{t("notifications.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {format(dateRange.from, "MMM d", { locale: dateFnsLocale })} - {format(dateRange.to, "MMM d", { locale: dateFnsLocale })}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.from, to: dateRange.to }}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      setDateRange({ from: range.from, to: range.to });
                    }
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b bg-muted/30">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("notifications.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={selectedDevice} onValueChange={setSelectedDevice}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("notifications.allDevices")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("notifications.allDevices")}</SelectItem>
              {devices.map((device) => (
                <SelectItem key={device.id} value={device.id.toString()}>
                  {device.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("notifications.allTypes")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("notifications.allTypes")}</SelectItem>
              {eventTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(`notifications.events.${type}`) || type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 pt-3">
            <TabsList>
              <TabsTrigger value="all" className="gap-2">
                <Bell className="h-4 w-4" />
                {t("notifications.tabs.all")}
                <Badge variant="secondary" className="ml-1">{stats.all}</Badge>
              </TabsTrigger>
              <TabsTrigger value="error" className="gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                {t("notifications.tabs.errors")}
                <Badge variant="destructive" className="ml-1">{stats.errors}</Badge>
              </TabsTrigger>
              <TabsTrigger value="warning" className="gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                {t("notifications.tabs.warnings")}
                <Badge variant="secondary" className="ml-1">{stats.warnings}</Badge>
              </TabsTrigger>
              <TabsTrigger value="info" className="gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                {t("notifications.tabs.info")}
                <Badge variant="outline" className="ml-1">{stats.info}</Badge>
              </TabsTrigger>
              <TabsTrigger value="configure" className="gap-2">
                <Settings2 className="h-4 w-4" />
                {t("notifications.config.tab")}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Event tabs content */}
          {["all", "error", "warning", "info"].includes(activeTab) && (
            <TabsContent value={activeTab} className="flex-1 overflow-hidden m-0 p-0">
              <ScrollArea className="h-full">
                <div className="p-4 space-y-2">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredEvents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <BellOff className="h-12 w-12 text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">{t("notifications.noEvents")}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("notifications.noEventsHint")}
                      </p>
                    </div>
                  ) : (
                    filteredEvents.map(renderEvent)
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          )}

          {/* Configure tab content */}
          <TabsContent value="configure" className="flex-1 overflow-hidden m-0 p-0">
            <ScrollArea className="h-full">
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">{t("notifications.config.title")}</h2>
                  </div>
                  <Button
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      setEditingRule(undefined);
                      setRuleDialogOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    {t("notifications.config.addRule")}
                  </Button>
                </div>

                {rulesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : notificationRules.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Bell className="h-12 w-12 text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">{t("notifications.config.noRules")}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("notifications.config.noRulesHint")}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("notifications.config.type")}</TableHead>
                          <TableHead>{t("notifications.config.channels")}</TableHead>
                          <TableHead>{t("notifications.config.always")}</TableHead>
                          <TableHead className="w-[100px]">{t("devices.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {notificationRules.map((rule) => (
                          <TableRow key={rule.id}>
                            <TableCell className="font-medium">
                              <div>{getTypeLabelForRule(rule.type)}</div>
                              {rule.type === 'deviceIdle' && rule.attributes?.minIdlePeriod && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {Math.round((rule.attributes.minIdlePeriod as number) / 60)} {t("notifications.config.idleTimeUnit")}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {rule.notificators
                                  ? rule.notificators.split(',').filter(Boolean).map((n) => (
                                      <Badge key={n} variant="secondary" className="capitalize text-xs">
                                        {n}
                                      </Badge>
                                    ))
                                  : <span className="text-muted-foreground text-sm">—</span>
                                }
                              </div>
                            </TableCell>
                            <TableCell>
                              {rule.always ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    setEditingRule(rule);
                                    setRuleDialogOpen(true);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => setDeletingRuleId(rule.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>

      {/* Rule dialog */}
      <NotificationRuleDialog
        open={ruleDialogOpen}
        onOpenChange={setRuleDialogOpen}
        rule={editingRule}
      />

      {/* Delete confirm dialog */}
      <AlertDialog open={deletingRuleId !== null} onOpenChange={(open) => !open && setDeletingRuleId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("notifications.config.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("notifications.config.deleteConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletingRuleId !== null && deleteMutation.mutate(deletingRuleId)}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("common.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Notifications;
